import { useEffect, useCallback } from "react";

import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { t } from "i18next";
import BasicDialog from "../../../../components/base/BasicDialog";
import { useToasts } from "../../../../hooks/useToasts";
import toast from "react-hot-toast";

import {
  useAdditionalEmailsForPersonId,
  useUpdateAdditialEmailsForPersonId,
  useAdditionalEmailsForCompanyId,
  useUpdateAdditialEmailsForCompanyId,
} from "../../../../services/customersService";
import { SnackbarSuccess } from "../../../../components/base/SnackbarAlert/SnackbarSuccess";

import EmailRecord from "./EmailRecord";

import { isEmptyObject, isEmptyArray } from "../../../../helpers/methods";

import { useForm } from "../../../../hooks/useForm";

const emptyObjectsList = [{}];

export function AdditionalEmailsDialog(props) {
  const { personId, companyId } = props;

  const {
    data: emailData,
    isLoading,
    isFetching,
    refetch,
  } = (
          props.formValue 
              ? {data: props.formValue, isLoading: false, isFetching: false} :
                 companyId ? useAdditionalEmailsForCompanyId(companyId)
                           : useAdditionalEmailsForPersonId(personId)
      )

  const { formValue, setFormValue, onChangeInArrayForm } = useForm();

  const title = t("clients.ClientPassportDialog.additional_emails");

  const { onFormErrors, onServerError } = useToasts();

  const { mutate: updateAdditionalEmails, isPending } = (
        companyId ?
            useUpdateAdditialEmailsForCompanyId(
                companyId,
                {
                    onSuccess: () => {
                        toast.custom(
                            () => <SnackbarSuccess message={t("successfullyEdited")} />,
                            {
                                position: "bottom-right",
                            },
                        );
                    },
                    onError: (error) => {
                        onServerError(error);
                    },
                }
            ) 
        :
            useUpdateAdditialEmailsForPersonId(
                personId,
                {
                    onSuccess: () => {
                        toast.custom(
                            () => <SnackbarSuccess message={t("successfullyEdited")} />,
                            {
                                position: "bottom-right",
                            },
                        );
                    },
                    onError: (error) => {
                        onServerError(error);
                    },
                }
            )
    );

  // Update form values when email data is loaded
  useEffect(() => {
    if (isLoading) {
      return;
    }
    setFormValue(
      !emailData || isEmptyArray(emailData) ? emptyObjectsList : emailData,
    );
  }, [isLoading]);

  const handleDelete = useCallback((index) => {
    setFormValue((prev) => {
      let newFormValue = [...prev];
      if (newFormValue.length !== 1) {
        newFormValue.splice(index, 1);
      } else {
        newFormValue.splice(index, 1, {});
      }

      return newFormValue;
    });
  }, []);

  const handleAdd = useCallback(() => {
    setFormValue((prev) => [...prev, {}]);
  }, []);

  const handleChangeInArrayForm = useCallback((...inputProps) => {
    onChangeInArrayForm(...inputProps);
  }, []);

  const prepareDataToSend = () => {
    return formValue
      .filter(
        (emailData) => !isEmptyObject(emailData) && emailData.email_address,
      )
      .map((emailData) => emailData.email_address);
  };

  const handleSubmit = () => {
    if (props.onSetFormValue) {
      props.onSetFormValue(prepareDataToSend());
      props.onClose();
      return;
    }
    updateAdditionalEmails(prepareDataToSend());
  };

  const formIsValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return prepareDataToSend().every((emailAddress) =>
      emailRegex.test(emailAddress),
    );
  };

  if (isLoading || isFetching || !formValue) {
    return (
      <BasicDialog
        open={props.open}
        onClose={props.onClose}
        titleAlign={"center"}
        contentAlign={"center"}
        title="Loading..."
        maxWidth={"xs"}
        showDialogActions
        alwaysFullScreen={false}
      >
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </BasicDialog>
    );
  }
  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign={"center"}
      contentAlign={"center"}
      showTitle={false}
      title={title}
      maxWidth={"xs"}
      showDialogActions
      showTopFullScreenButton={false}
      alwaysFullScreen={false}
      contentSx={{ backgroundColor: "#f5f5f5" }}
      headerComponent={
        <Grid
          container
          direction={"row"}
          alignItems={"center"}
          justifyContent={"center"}
          sx={{ p: 2, backgroundColor: "#f5f5f5" }}
          gap={3}
        >
          <Typography
            variant={"h4"}
            sx={{ fontSize: "2rem", ontWeight: "bold" }}
          >
            {title}
          </Typography>
        </Grid>
      }
    >
      <Grid
        container
        rowSpacing={1}
        columnSpacing={1}
        alignItems={"flex-start"}
      >
        <Grid item xs={12} container rowSpacing={2}>
          {formValue.map((emailData, index) => (
            <EmailRecord
              key={index}
              readOnly={props.readOnly}
              index={index}
              showAddButton={!props.readOnly && index === 0}
              emailAddress={emailData.email_address}
              onAdd={handleAdd}
              onDelete={handleDelete}
              onChange={handleChangeInArrayForm}
            />
          ))}
        </Grid>
        {!props.readOnly && (
          <Grid item xs={12}>
            <LoadingButton
              loading={isFetching || isPending}
              disabled={!formIsValid()}
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={handleSubmit}
              fullWidth
            >
              {t("clients.passportDialogCompany.saveChanges")}
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    </BasicDialog>
  );
}
